import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import {
  Leaf,
  CheckCircle2,
  Circle,
  Loader2,
  Clock,
  AlertCircle,
  Sun,
  ArrowRight,
  Hourglass,
} from "lucide-react";
import ChatWidget from "../components/public/ChatWidget";

// Status icon for an activity
function ActivityIcon({ status }) {
  if (status === "completed") return <CheckCircle2 className="w-4 h-4 text-[#1F4E2C]" strokeWidth={2.5} />;
  if (status === "in_process") return <Loader2 className="w-4 h-4 text-[#D4A93A] animate-spin" />;
  if (status === "skipped") return <Circle className="w-4 h-4 text-[#94A3B8]" strokeWidth={2} />;
  return <Circle className="w-4 h-4 text-[#94A3B8]" strokeWidth={2} />;
}

function StatusPill({ status }) {
  const map = {
    completed: { bg: "bg-[#1F4E2C]", fg: "text-[#D4FF7A]", label: "Done" },
    in_process: { bg: "bg-[#D4A93A]/15", fg: "text-[#7A5C00]", label: "In progress" },
    pending: { bg: "bg-[#E2E8D8]", fg: "text-[#3D5A48]", label: "Pending" },
    skipped: { bg: "bg-[#F2F4EE]", fg: "text-[#94A3B8]", label: "Skipped" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function PublicTrack() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("No tracker token provided. Please use the link from your email or WhatsApp.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/track/${encodeURIComponent(token)}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err.response?.data?.detail) || err.message || "Could not load tracker.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ── LOADING ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F4EE] flex items-center justify-center">
        <div className="text-center" data-testid="track-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#1F4E2C] mx-auto" />
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#3D5A48] mt-3">
            Loading your project…
          </div>
        </div>
      </div>
    );
  }

  // ── ERROR ───────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#F2F4EE]">
        <div className="mobile-frame bg-white" data-testid="track-error">
          <div className="px-6 py-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#FEF2F2] mx-auto flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-[#B91C1C]" strokeWidth={2} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B91C1C] mt-6">
              Link issue
            </div>
            <h1 className="font-display text-3xl font-black tracking-tighter mt-3 text-[#0E2818]">
              Can't open<br /><span className="italic font-light">your tracker.</span>
            </h1>
            <p className="text-sm text-[#3D5A48] mt-4 max-w-sm mx-auto">
              {error || "Unknown error"}
            </p>
            <p className="text-xs text-[#94A3B8] mt-6">
              Reply to your project email and we'll send a fresh link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── DATA ────────────────────────────────────────────────────────────
  const {
    stage,
    customer_name,
    opportunity_name,
    phases = [],
    overall_pct = 0,
    status,
    current_phase,
    appointment_at,
  } = data;
  const isLeadOnly = stage === "lead";

  return (
    <div className="min-h-screen bg-[#F2F4EE]">
      <div className="mobile-frame bg-white" data-testid="track-page">
        {/* HERO */}
        <div className="relative overflow-hidden bg-[#0E2818] text-white">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[#7DD181]/30 blur-3xl"></div>
          <div className="absolute right-8 -bottom-8 w-40 h-40 rounded-full bg-[#D4FF7A]/30 blur-2xl"></div>
          <div className="relative px-6 pt-8 pb-10">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#7DD181] flex items-center justify-center">
                <Leaf className="w-5 h-5 text-[#0E2818]" strokeWidth={2.5} fill="#0E2818" />
              </div>
              <div>
                <div className="font-display text-base leading-none font-bold">Purvodaya Solar.</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7DD181] mt-0.5">
                  {isLeadOnly ? "Enquiry Received" : "Project Tracker"}
                </div>
              </div>
            </div>

            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7DD181] mt-8 flex items-center gap-2">
              <Sun className="w-3 h-3" /> Hello, {customer_name}
            </div>
            <h1
              className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-[0.95] mt-3"
              data-testid="track-opp-name"
            >
              {opportunity_name}
            </h1>

            {/* Progress bar (only for opportunity stage) */}
            {!isLeadOnly && (
              <div className="mt-7">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7DD181]/80">Overall progress</div>
                    <div
                      className="font-display text-4xl font-black text-[#D4FF7A] leading-none mt-1"
                      data-testid="track-progress-pct"
                    >
                      {overall_pct}<span className="text-xl text-white/70">%</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#7DD181]/80">Status</div>
                    <div className="font-mono text-xs uppercase tracking-widest text-white mt-1">{status}</div>
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-[#1F4E2C] relative">
                  <div
                    className="absolute left-0 top-0 bottom-0 bg-[#D4FF7A] transition-all"
                    style={{ width: `${overall_pct}%` }}
                    data-testid="track-progress-bar"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* LEAD-ONLY (Awaiting consultant assignment) */}
        {isLeadOnly && (
          <div className="px-6 py-8" data-testid="track-lead-stage">
            <div className="bg-[#F2F4EE] border border-[#D5DCC9] p-5 flex items-start gap-3">
              <Hourglass className="w-5 h-5 text-[#1F4E2C] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg font-bold text-[#0E2818] leading-tight">
                  Awaiting consultant assignment
                </div>
                <p className="text-sm text-[#3D5A48] mt-1.5 leading-relaxed">
                  Your enquiry has been received and is being reviewed by our team. A solar consultant
                  will reach out shortly to confirm your site visit.
                </p>
                {appointment_at && (
                  <div className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest bg-[#0E2818] text-[#D4FF7A] px-2 py-1">
                    <Clock className="w-3 h-3" /> Requested slot · {fmtDateTime(appointment_at)}
                  </div>
                )}
              </div>
            </div>

            {/* Five-phase preview */}
            <div className="mt-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1F4E2C]">
                What's next — the 5-phase journey
              </div>
              <ol className="mt-4 space-y-2.5">
                {[
                  "Due diligence & site visit",
                  "Documentation & approvals",
                  "Subsidy registration",
                  "Installation & commissioning",
                  "Subsidy disbursement & handover",
                ].map((label, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 border-l-2 border-[#E2E8D8] pl-3 py-1"
                    data-testid={`track-phase-preview-${i + 1}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-white border-2 border-[#E2E8D8] flex items-center justify-center font-display text-[10px] font-black text-[#94A3B8]">
                      {i + 1}
                    </div>
                    <div className="text-sm text-[#0E2818]">{label}</div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-8 bg-[#0E2818] text-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7DD181]">
                Talk to Surya
              </div>
              <div className="font-display text-xl font-bold mt-2 leading-tight">
                Your digital assistant is ready to help — tap the chat icon to ask about your enquiry.
              </div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#7DD181]/70 flex items-center gap-1.5">
                <ArrowRight className="w-3 h-3" /> Purvodaya Solar · Solar projects, end-to-end
              </div>
            </div>
          </div>
        )}

        {/* OPPORTUNITY PHASES */}
        {!isLeadOnly && (
          <div className="px-6 py-7" data-testid="track-phases">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#1F4E2C]">
              Five phases — your project journey
            </div>

            <div className="mt-5 space-y-5">
              {phases.map((p, i) => {
                const isCurrent = p.phase === current_phase;
                const allDone = p.total > 0 && p.done === p.total;
                return (
                  <div
                    key={p.phase}
                    className={`relative pl-8 ${i < phases.length - 1 ? "pb-5 border-l-2 ml-3" : "ml-3"} ${
                      allDone ? "border-[#1F4E2C]" : "border-[#E2E8D8]"
                    }`}
                    data-testid={`track-phase-${p.phase}`}
                  >
                    {/* Dot */}
                    <div
                      className={`absolute -left-[11px] top-0 w-5 h-5 rounded-full flex items-center justify-center ${
                        allDone
                          ? "bg-[#1F4E2C] text-[#D4FF7A]"
                          : isCurrent
                          ? "bg-[#D4FF7A] text-[#0E2818] ring-4 ring-[#D4FF7A]/30"
                          : "bg-white border-2 border-[#E2E8D8] text-[#94A3B8]"
                      }`}
                    >
                      {allDone ? (
                        <CheckCircle2 className="w-3 h-3" strokeWidth={3} />
                      ) : (
                        <span className="font-display text-[10px] font-black">{p.phase}</span>
                      )}
                    </div>

                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="font-display text-lg font-bold tracking-tight text-[#0E2818] leading-tight">
                          {p.phase_name}
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#3D5A48] mt-0.5">
                          {p.done} / {p.total} done{isCurrent && !allDone ? " · current" : ""}
                        </div>
                      </div>
                      {isCurrent && !allDone && (
                        <span className="font-mono text-[9px] uppercase tracking-widest bg-[#D4FF7A] text-[#0E2818] px-1.5 py-0.5">
                          Live
                        </span>
                      )}
                    </div>

                    {/* Activities */}
                    <ul className="mt-3 space-y-2">
                      {p.activities.map((a, ai) => (
                        <li key={ai} className="flex items-start gap-2.5">
                          <div className="mt-0.5"><ActivityIcon status={a.status} /></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-xs text-[#0E2818] truncate">{a.name}</div>
                              <StatusPill status={a.status} />
                            </div>
                            {a.appointment_at && (
                              <div className="font-mono text-[10px] text-[#3D5A48] mt-0.5 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {fmtDate(a.appointment_at)}
                              </div>
                            )}
                            {a.completed_at && a.status === "completed" && (
                              <div className="font-mono text-[10px] text-[#94A3B8] mt-0.5">
                                Completed · {fmtDate(a.completed_at)}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            {/* Help block */}
            <div className="mt-10 bg-[#0E2818] text-white p-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7DD181]">Questions?</div>
              <div className="font-display text-xl font-bold mt-2 leading-tight">
                Tap the chat icon — Surya, your digital assistant, has live answers about your project.
              </div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-[#7DD181]/70 flex items-center gap-1.5">
                <ArrowRight className="w-3 h-3" /> Purvodaya Solar · Solar projects, end-to-end
              </div>
            </div>
          </div>
        )}

        {/* footer */}
        <div className="px-6 py-6 border-t border-[#E2E8D8] flex items-center justify-center text-[10px] font-mono uppercase tracking-widest text-[#3D5A48]">
          <div className="flex items-center gap-1.5">
            <Leaf className="w-3 h-3 text-[#1F4E2C]" /> Powered by Purvodaya Solar · 2026
          </div>
        </div>
      </div>

      {/* Digital assistant — contextual, tied to this customer's token */}
      <ChatWidget token={token} customerName={customer_name} />
    </div>
  );
}
